import { useCallback, useEffect, useMemo } from "react";

import {
  canonicalWorkingScopeSearch,
  useWorkingScope,
  withWorkingScopeSearch,
  type WorkingScopeSearch,
} from "../lib/workingScope";
import { useCallerAuthority } from "../lib/authority";
import { canAuthorDashboards } from "../lib/dashboardAuthorization";
import { rangeForPreset, type RangePreset } from "../lib/dashboardMetrics";
import { requestErrorDescription } from "../lib/errors";
import {
  DASHBOARD_DEFAULT_RANGE,
  parseDashboardDetailSearch,
  type DashboardDetailSearch,
} from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import {
  dashboardWidgets,
  useDashboardBoards,
  useDashboardParam,
} from "../lib/useDashboardBoards";
import { useDashboardWidgetActions } from "../lib/useDashboardWidgetActions";
import { useDashboardMarkerNames } from "../lib/useDashboardMarkerNames";
import { Button, EmptyState, InlineError, Page, Skeleton, Stack } from "../ui";
import { DashboardPageHeader } from "./DashboardHeaderControls";
import { DashboardWidgetGrid } from "./DashboardWidgetGrid";
import { DashboardWidgetEditor } from "./DashboardWidgetEditor";
import { DashboardWidgetTile } from "./DashboardWidgetTile";

const NEW_WIDGET_SUBJECT = "new";

type DashboardSearchState = DashboardDetailSearch & WorkingScopeSearch;

export function Dashboard() {
  const { agentId } = useWorkingScope();
  const dashboardId = useDashboardParam();
  const navigate = useTypedNavigate<
    DashboardSearchState,
    "/dashboard/$dashboardId",
    { dashboardId: string }
  >();
  const authority = useCallerAuthority();
  const search = useTypedSearch(
    withWorkingScopeSearch(parseDashboardDetailSearch),
  );
  const range = search.range ?? DASHBOARD_DEFAULT_RANGE;
  const canAuthor = canAuthorDashboards(authority);
  const isLayoutEditing = Boolean(dashboardId) && search.edit === "layout";
  const widgetSubject = dashboardId ? search.widget : undefined;

  const boards = useDashboardBoards({ agentId, dashboardId });
  const markerNames = useDashboardMarkerNames(agentId);
  const dashboardsData = boards.dashboards.data;
  const savedDashboards = useMemo(() => dashboardsData ?? [], [dashboardsData]);
  const selectedDashboard = dashboardId
    ? savedDashboards.find((dashboard) => dashboard.id === dashboardId)
    : undefined;
  const widgets = useMemo(
    () => dashboardWidgets(selectedDashboard),
    [selectedDashboard],
  );
  const window = useMemo(() => rangeForPreset(range), [range]);

  // Functional-merge navigation ONLY so the root `?agent` scope survives.
  const setSearchParam = useCallback(
    (patch: Partial<DashboardSearchState>, replace = false) => {
      void navigate({
        replace,
        search: mergeSearch<DashboardSearchState>(patch),
      });
    },
    [navigate],
  );

  const onRangeChange = useCallback(
    (next: RangePreset) => {
      setSearchParam({
        range: next === DASHBOARD_DEFAULT_RANGE ? undefined : next,
      });
    },
    [setSearchParam],
  );

  // Board switches carry canonical scope + range — edit state drops.
  const selectBoard = useCallback(
    (boardId: string) => {
      void navigate({
        params: { dashboardId: boardId },
        search: (prev: DashboardSearchState): DashboardSearchState => ({
          ...canonicalWorkingScopeSearch(prev),
          range: prev.range,
        }),
        to: "/dashboard/$dashboardId",
      });
    },
    [navigate],
  );

  const openWidgetEditor = useCallback(
    (subject: string) => {
      setSearchParam({ widget: subject });
    },
    [setSearchParam],
  );
  // `replace: true` so Back never reopens a dismissed drawer (§4a).
  const closeWidgetEditor = useCallback(() => {
    setSearchParam({ widget: undefined }, true);
  }, [setSearchParam]);
  const enterLayoutEdit = useCallback(() => {
    setSearchParam({ edit: "layout" });
  }, [setSearchParam]);
  const exitLayoutEdit = useCallback(() => {
    setSearchParam({ edit: undefined }, true);
  }, [setSearchParam]);

  const actions = useDashboardWidgetActions({
    boards,
    dashboardId,
    isLayoutEditing,
    widgets,
    onLayoutSaved: exitLayoutEdit,
    onWidgetSaved: closeWidgetEditor,
  });

  // `?edit=` / `?widget=` are author-only: strip for non-authors (deep links
  // included) once the authority seam resolves.
  useEffect(() => {
    if (
      (search.edit !== undefined || search.widget !== undefined) &&
      !authority.isPending &&
      !canAuthor
    ) {
      setSearchParam({ edit: undefined, widget: undefined }, true);
    }
  }, [
    authority.isPending,
    canAuthor,
    search.edit,
    search.widget,
    setSearchParam,
  ]);

  // `/dashboard` with no id resolves to the first saved board.
  useEffect(() => {
    if (!dashboardId && !boards.dashboards.isPending && savedDashboards[0]) {
      void navigate({
        params: { dashboardId: savedDashboards[0].id },
        replace: true,
        search: (prev: DashboardSearchState) => prev,
        to: "/dashboard/$dashboardId",
      });
    }
  }, [boards.dashboards.isPending, dashboardId, navigate, savedDashboards]);

  const widgetsById = useMemo(
    () => new Map(widgets.map((widget) => [widget.id, widget])),
    [widgets],
  );
  const tileLabels = useMemo(
    () =>
      Object.fromEntries(widgets.map((widget) => [widget.id, widget.title])),
    [widgets],
  );
  const editorWidget =
    widgetSubject && widgetSubject !== NEW_WIDGET_SUBJECT
      ? widgetsById.get(widgetSubject)
      : undefined;

  const hasBoards = savedDashboards.length > 0;
  const isListPending = boards.dashboards.isPending;
  // The global agent selector is the ONLY data scope: a board's `agent_id` is
  // ownership/grouping, never a silent per-board data pin.
  const dataAgentId = agentId;

  return (
    <Page variant="workspace">
      <DashboardPageHeader
        agentId={agentId}
        boards={boards}
        canAuthor={canAuthor}
        dashboardId={dashboardId}
        isLayoutEditing={isLayoutEditing}
        isSavePending={boards.patchConfig.isPending}
        range={range}
        savedDashboards={savedDashboards}
        selectedDashboard={selectedDashboard}
        onAddWidget={() => {
          openWidgetEditor(NEW_WIDGET_SUBJECT);
        }}
        onCancelLayoutEdit={exitLayoutEdit}
        onEnterLayoutEdit={enterLayoutEdit}
        onRangeChange={onRangeChange}
        onSaveLayout={actions.saveLayout}
        onSelectBoard={selectBoard}
      />

      <Stack gap={6}>
        {boards.dashboards.isError ? (
          <InlineError>
            {requestErrorDescription(
              boards.dashboards.error,
              "Dashboard request failed.",
            )}
          </InlineError>
        ) : null}
        {authority.isError ? (
          <InlineError>{authority.errorMessage}</InlineError>
        ) : null}

        {isListPending ? (
          <Skeleton label="Loading dashboards" rows={4} shape="detail-field" />
        ) : null}

        {!isListPending && !hasBoards ? (
          <EmptyState
            description={
              canAuthor ? undefined : "A member or above can create one."
            }
            title="No dashboards"
          />
        ) : null}

        {selectedDashboard && widgets.length === 0 ? (
          <EmptyState
            action={
              canAuthor ? (
                <Button
                  onClick={() => {
                    openWidgetEditor(NEW_WIDGET_SUBJECT);
                  }}
                >
                  Add widget
                </Button>
              ) : null
            }
            title="No widgets"
          />
        ) : null}

        {selectedDashboard && widgets.length > 0 ? (
          <DashboardWidgetGrid
            editable={isLayoutEditing}
            items={actions.gridItems}
            renderTile={(id) => {
              const widget = widgetsById.get(id);
              if (!widget) {
                return null;
              }
              return (
                <DashboardWidgetTile
                  agentId={dataAgentId}
                  canAuthor={canAuthor && !isLayoutEditing}
                  markerDisplayNames={markerNames.displayNames}
                  widget={widget}
                  window={window}
                  onDuplicate={actions.onDuplicateWidget}
                  onEdit={(subject) => {
                    openWidgetEditor(subject.id);
                  }}
                  onRemove={actions.onRemoveWidget}
                />
              );
            }}
            tileLabels={tileLabels}
            onLayoutChange={actions.onLayoutChange}
          />
        ) : null}
      </Stack>

      {canAuthor && selectedDashboard ? (
        <DashboardWidgetEditor
          agentId={dataAgentId}
          error={
            boards.patchConfig.isError
              ? requestErrorDescription(
                  boards.patchConfig.error,
                  "Dashboard request failed.",
                )
              : null
          }
          isOpen={widgetSubject !== undefined}
          isPending={boards.patchConfig.isPending}
          markerDisplayNames={markerNames.displayNames}
          markerOptions={markerNames.options}
          widget={editorWidget}
          window={window}
          onCancel={closeWidgetEditor}
          onSave={actions.onSaveWidget}
        />
      ) : null}
    </Page>
  );
}
